import { useCallback, useEffect } from 'react';
import { Vector2 } from 'three';
import { useShallow } from 'zustand/shallow';

import { isInputElement } from 'app/modules/wallforming/components/scenes/objects/utils/sceneUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { MAIN_DIV_ID } from 'shared/constants';

const ndcPosition = new Vector2();
const lastPointerPosition = { x: 0, y: 0 };

/**
 * Handles keyboard events for article selection, copy, and paste actions.
 */
export const useArticleEventHandler = () => {
    const {
        isAutoDragActiveRef,
        isDraggingActive,
        discardArticleInstanceSelection,
        copyArticleInstances,
        pasteArticleInstances,
        cutArticleInstances,
        selectAllArticleInstances,
        updatePivotControls,
        handleOnArticleDefaultRotation,
        setIsGroupPanelOpen,
        handleOnUngroupSelection,
        mainWebGLRendererRef
    } = usePlacementStore(
        useShallow((state) => ({
            isAutoDragActiveRef: state.isAutoDragActiveRef,
            isDraggingActive: state.isDraggingActive,
            discardArticleInstanceSelection: state.discardArticleInstanceSelection,
            copyArticleInstances: state.copyArticleInstances,
            pasteArticleInstances: state.pasteArticleInstances,
            selectAllArticleInstances: state.selectAllArticleInstances,
            updatePivotControls: state.updatePivotControls,
            handleOnArticleDefaultRotation: state.handleOnArticleDefaultRotation,
            setIsGroupPanelOpen: state.setIsGroupPanelOpen,
            handleOnUngroupSelection: state.handleOnUngroupSelection,
            cutArticleInstances: state.cutArticleInstances,
            mainWebGLRendererRef: state.mainWebGLRendererRef
        }))
    );

    useEffect(() => {
        const handleOnPointerMove = (event: PointerEvent) => {
            lastPointerPosition.x = event.clientX;
            lastPointerPosition.y = event.clientY;
        };

        window.addEventListener('pointermove', handleOnPointerMove);
        return () => window.removeEventListener('pointermove', handleOnPointerMove);
    }, []);

    const handleOnSelectionChange = useCallback(
        (e: KeyboardEvent) => {
            if ((e.target as HTMLDivElement)?.id !== MAIN_DIV_ID) {
                return;
            }

            if (e.key === 'Delete') {
                discardArticleInstanceSelection(true);
            }

            if (e.ctrlKey && e.code === 'KeyA' && !isAutoDragActiveRef.current && !isDraggingActive()) {
                e.preventDefault();
                selectAllArticleInstances();
            }
        },
        [discardArticleInstanceSelection, isAutoDragActiveRef, isDraggingActive, selectAllArticleInstances]
    );

    const handleOnCopyAndPaste = useCallback(
        (e: KeyboardEvent) => {
            if (!e.ctrlKey && !e.metaKey) {
                return;
            }

            if ((e.target as HTMLDivElement)?.id !== MAIN_DIV_ID) {
                return;
            }

            if (isAutoDragActiveRef.current || isDraggingActive()) {
                return;
            }

            if (e.code === 'KeyC') {
                e.preventDefault();
                copyArticleInstances();
            } else if (e.code === 'KeyV') {
                e.preventDefault();
                const canvas = mainWebGLRendererRef.current?.domElement;
                if (canvas) {
                    const rect = canvas.getBoundingClientRect();
                    ndcPosition.set(
                        ((lastPointerPosition.x - rect.left) / rect.width) * 2 - 1,
                        -((lastPointerPosition.y - rect.top) / rect.height) * 2 + 1
                    );
                    pasteArticleInstances(ndcPosition);
                }
            } else if (e.code === 'KeyX') {
                e.preventDefault();
                cutArticleInstances();
            }
        },
        [
            isAutoDragActiveRef,
            isDraggingActive,
            copyArticleInstances,
            pasteArticleInstances,
            cutArticleInstances,
            mainWebGLRendererRef
        ]
    );

    const handleOnTransformation = useCallback(
        (e: KeyboardEvent) => {
            if (e.code === 'KeyT' && !isAutoDragActiveRef.current && !isDraggingActive()) {
                updatePivotControls();
            }

            if (e.code === 'KeyR') {
                handleOnArticleDefaultRotation();
            }
        },
        [isAutoDragActiveRef, isDraggingActive, updatePivotControls, handleOnArticleDefaultRotation]
    );

    const handleOnGroupChange = useCallback(
        (e: KeyboardEvent) => {
            if (isAutoDragActiveRef.current || isDraggingActive()) {
                return;
            }

            if (e.code === 'KeyG' && e.ctrlKey && e.shiftKey) {
                e.preventDefault();
                handleOnUngroupSelection();
                return;
            }

            if (e.code === 'KeyG' && e.ctrlKey) {
                e.preventDefault();
                setIsGroupPanelOpen(true);
            }
        },
        [handleOnUngroupSelection, isAutoDragActiveRef, isDraggingActive, setIsGroupPanelOpen]
    );

    const handleOnKeyDown = useCallback(
        (e: KeyboardEvent) => {
            const target = e.target as HTMLElement;
            if (isInputElement(target)) {
                return;
            }

            handleOnTransformation(e);
            handleOnCopyAndPaste(e);
            handleOnSelectionChange(e);
            handleOnGroupChange(e);
        },
        [handleOnTransformation, handleOnCopyAndPaste, handleOnSelectionChange, handleOnGroupChange]
    );

    useEffect(() => {
        window.addEventListener('keydown', handleOnKeyDown);
        return () => window.removeEventListener('keydown', handleOnKeyDown);
    }, [handleOnKeyDown]);
};
